import * as React from "react";
import { convertCamelCaseToText } from "../../global/utility/Utils";
import { RANDOM_COLORS, SEARCH_STATUS_PAYLOAD_DONE, SEARCH_STATUS_PAYLOAD_UPLOADING, SEARCH_STATUS_PAYLOAD_SEARCHING } from "./SearchConstants";

export const getStatusOfSearch = (result) => {
  if (result?.status === SEARCH_STATUS_PAYLOAD_UPLOADING) return "Fetching...";
  if (result?.status === SEARCH_STATUS_PAYLOAD_SEARCHING) return "Searching...";
  let searchStatus = result && result?.status ? convertCamelCaseToText(result?.status == "reset" ? "analyzing" : result?.status): "Failed";
  searchStatus = searchStatus == "done" ? `${result?.totalhits || 0}` : searchStatus;
  return searchStatus;
};

// A search's hit count is known/final once it's uploading or done - fail/searching have no count yet.
const isCountableStatus = (status) => status === SEARCH_STATUS_PAYLOAD_DONE || status === SEARCH_STATUS_PAYLOAD_UPLOADING;

// Maps a search's status to a badge color variant for the results-count box: "success" | "error" | "pending"
export const getSearchStatusVariant = (result) => {
  const status = result?.status;
  if (status === "fail") return "error";
  if (isCountableStatus(status)) return "success";
  return "pending";
};

// Text shown in the recent-searches list row: hit count once known, otherwise the status text.
export const getListRowResultText = (result) => {
  if (isCountableStatus(result?.status)) {
    return `${result?.totalhits ?? 0}`;
  }
  return getStatusOfSearch(result);
};

// The static "results" label follows an actual number, not status text.
export const hasCountableResults = (result) => isCountableStatus(result?.status);

export const getColorNameFromHex = (hexlist) => {
    if(hexlist.length === 0) return "";
    const colorObj = RANDOM_COLORS.filter((color) => hexlist.includes(color.hex));
    return colorObj.length > 0 ? colorObj.map((color) => color.name).join(", ") :"";
  };

const formatFilterValues = (str) =>
  str ? str.split(",").map((v) => convertCamelCaseToText(v.trim())).join(", ") : "";

// Converts an array of color values (lowercase names or hex strings) to display names.
const getColorDisplayNames = (colorList) => {
  if (!colorList?.length) return "";
  return colorList.map((value) => {
    const match = RANDOM_COLORS.find(
      (c) => c.hex.toLowerCase() === String(value).toLowerCase() ||
             c.name.toLowerCase() === String(value).toLowerCase()
    );
    return match ? match.name : String(value);
  }).join(", ");
};

export const buildPersonFilterLabel = (filters) => {
  // API may return keys as lowercase (genderai) or camelCase (genderAi) depending on version.
  const genderAi = filters?.genderAi ?? filters?.genderai;
  const ageAi = filters?.ageAi ?? filters?.ageai;
  const colorHigh = filters?.colorHigh ?? filters?.colorhigh;
  const colorLow = filters?.colorLow ?? filters?.colorlow;

  const hasGender = genderAi?.length > 0;
  const hasAge = ageAi?.length > 0;
  const colorHighName = colorHigh?.length > 0 ? getColorDisplayNames(colorHigh) : "";
  const colorLowName = colorLow?.length > 0 ? getColorDisplayNames(colorLow) : "";

  const identityParts = [];
  if (!hasGender) identityParts.push("Person");
  if (hasAge) identityParts.push(formatFilterValues(ageAi));
  if (hasGender) identityParts.push(formatFilterValues(genderAi));

  const parts = [];
  if (identityParts.length > 0) parts.push(identityParts.join(", "));
  if (colorHighName) parts.push("with Upper Color " + colorHighName);
  if (colorLowName) parts.push("and Lower Color " + colorLowName);
  return parts.join(" ");
};

// Returns true if the presigned S3 URL has not yet expired.
// Reads X-Amz-Date (signing time) and X-Amz-Expires (TTL in seconds) from the URL query params.
export const isS3UrlValid = (s3url) => {
  if (!s3url) return false;
  try {
    const params = new URL(s3url).searchParams;
    const amzDate = params.get('X-Amz-Date');     
    const amzExpires = params.get('X-Amz-Expires'); 
    if (!amzDate || !amzExpires) return false;
    const signedAt = new Date(
      `${amzDate.slice(0, 4)}-${amzDate.slice(4, 6)}-${amzDate.slice(6, 8)}` +
      `T${amzDate.slice(9, 11)}:${amzDate.slice(11, 13)}:${amzDate.slice(13, 15)}Z`
    ).getTime();
    return Date.now() < signedAt + parseInt(amzExpires, 10) * 1000;
  } catch {
    return false;
  }
};

// Flattens response[i].results from a nested CCD → searchIndex → entry tree
// into a flat array where each element is a single image entry with its parent
// ccdId, ccdqr_code, and fovId lifted to the top level.
export const flattenSearchResults = (search) => {
  const flatEntries = [];
  (search.results || []).forEach((result) => {
    (result.searchIndexes || []).forEach((searchIndex) => {
      (searchIndex.entries || []).forEach((entry) => {
        flatEntries.push({
          ccdqr_code: result.ccdqr_code,
          ccdId: result.ccdId,
          fovId: searchIndex.fovId,
          ...entry,
        });
      });
    });
  });
  flatEntries.sort((a, b) => b.timestamp - a.timestamp);
  return { ...search, results: flatEntries };
};

